export function toLocalDayDate(dayKey: string) {
  const [year, month, day] = dayKey.split("-").map(Number);
  return new Date(year!, (month ?? 1) - 1, day!);
}

export function pad2(n: number) {
  return String(n).padStart(2, "0");
}

export function getMinuteBoundsForDay(dayTimeframes: { start: Date; end: Date }[]) {
  if (dayTimeframes.length === 0)
    return { startMinute: 8 * 60, endMinute: 18 * 60 };

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const tf of dayTimeframes) {
    const startMin = tf.start.getHours() * 60 + tf.start.getMinutes();
    const endMin = tf.end.getHours() * 60 + tf.end.getMinutes();
    min = Math.min(min, startMin);
    max = Math.max(max, endMin);
  }

  // Align to 15-minute boundaries so days can start/end at :15/:30/:45.
  const startMinute = Math.floor(min / 15) * 15;
  const endMinute = Math.ceil(max / 15) * 15 || startMinute + 15;

  return { startMinute, endMinute: endMinute <= startMinute ? startMinute + 60 : endMinute };
}

export function buildQuarterHourTicks(startMinute: number, endMinute: number) {
  const ticks: number[] = [];
  for (let m = startMinute; m < endMinute; m += 15)
    ticks.push(m);
  return ticks;
}

/**
 * Generates quarter-hour ticks only within the bounds of each individual
 * timeframe rather than spanning the full min-to-max range. This avoids
 * rendering empty slots in gaps between non-contiguous timeframes.
 */
export function buildQuarterHourTicksForDay(dayTimeframes: { start: Date; end: Date }[]): {
  ticks: number[];
  rangeStarts: Set<number>;
} {
  if (dayTimeframes.length === 0) {
    return { ticks: [], rangeStarts: new Set() };
  }

  const tickSet = new Set<number>();
  const rangeStarts = new Set<number>();

  for (const tf of dayTimeframes) {
    const startMin = tf.start.getHours() * 60 + tf.start.getMinutes();
    const endMin = tf.end.getHours() * 60 + tf.end.getMinutes();

    const alignedStart = Math.floor(startMin / 15) * 15;
    let alignedEnd = Math.ceil(endMin / 15) * 15 || alignedStart + 15;
    if (alignedEnd <= alignedStart)
      alignedEnd = alignedStart + 60;

    rangeStarts.add(alignedStart);

    for (let m = alignedStart; m < alignedEnd; m += 15) {
      tickSet.add(m);
    }
  }

  const ticks = Array.from(tickSet).sort((a, b) => a - b);
  return { ticks, rangeStarts };
}

const QUARTER_HOUR_MS = 15 * 60 * 1000;

export function countSlotOverlaps(
  slotStart: Date,
  dayTimeframes: { start: Date; end: Date }[],
) {
  const slotStartMs = slotStart.getTime();
  const slotEndMs = slotStartMs + QUARTER_HOUR_MS;
  let count = 0;

  for (const tf of dayTimeframes) {
    const startMs = tf.start.getTime();
    const endMs = tf.end.getTime();
    if (slotStartMs >= startMs && slotEndMs <= endMs)
      count += 1;
  }

  return count;
}
